import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Loader2, Ban, AlertTriangle } from "lucide-react";

/**
 * Admin-only dialog to cancel an order with a mandatory reason.
 * Calls onConfirm({ trackingId, reason }) on submit.
 */
export default function AdminCancelOrderDialog({ order, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const open = !!order;
  const trim = reason.trim();
  const valid = trim.length >= 5 && trim.length <= 500;

  const handleConfirm = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onConfirm({ trackingId: order.tracking_id, reason: trim });
      setReason("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setReason(""); onClose(); } }}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="admin-cancel-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-600">
            <AlertTriangle className="h-5 w-5" />
            Cancel Order
          </DialogTitle>
        </DialogHeader>
        {order && (
          <div className="space-y-4">
            <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">Tracking ID</span>
                <span className="font-mono font-bold text-slate-900">{order.tracking_id}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-500">Current status</span>
                <span className="font-semibold text-slate-700">{order.status}</span>
              </div>
              {order.user_email && (
                <div className="flex justify-between mt-1">
                  <span className="text-slate-500">Customer</span>
                  <span className="text-slate-700">{order.user_email}</span>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                Reason for cancellation <span className="text-rose-500">*</span>
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Customer requested cancellation, payment failed, address invalid…"
                rows={4}
                maxLength={500}
                className="rounded-xl text-sm"
                data-testid="cancel-reason-input"
                autoFocus
              />
              <p className="text-[10px] text-slate-400 mt-1">
                {trim.length}/500 (minimum 5 characters). Visible to the customer on the tracking page.
              </p>
            </div>
          </div>
        )}
        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => { setReason(""); onClose(); }}
            className="rounded-xl"
            data-testid="cancel-dialog-back"
          >
            Keep Order
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!valid || submitting}
            className="rounded-xl bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50"
            data-testid="cancel-dialog-confirm"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Ban className="h-4 w-4 mr-2" />}
            Cancel Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
